"use client";

import { useEffect } from "react";
import { Button } from "@/components/ui/button/Button";

export default function FullWidthError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log error to error tracking service
    // TODO(FRONTEND-007): Integrate Sentry for error tracking
    // Example: Sentry.captureException(error);
    if (process.env.NODE_ENV === "development") {
      console.error("Full-width pages error:", error);
    }
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md">
        <div className="mb-8">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-10 h-10 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-text-primary mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-text-secondary mb-8">
            We're sorry, but we encountered an unexpected error. Please try
            again or contact support if the problem persists.
          </p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={reset}
            variant="primary"
            size="lg"
            fullWidth
            className="mb-2"
          >
            Try again
          </Button>
          <Button
            onClick={() => (window.location.href = "/")}
            variant="secondary"
            size="lg"
            fullWidth
          >
            Go to homepage
          </Button>
        </div>

        {process.env.NODE_ENV === "development" && error.message && (
          <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
            <p className="text-sm font-semibold text-red-800 mb-2">
              Error Details (Development Only):
            </p>
            <p className="text-xs text-red-700 font-mono break-all">
              {error.message}
            </p>
            {error.digest && (
              <p className="text-xs text-red-600 mt-2">
                Error ID: {error.digest}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
